import api from './api';
// import axios from 'axios';

class TranslationAPI {
  constructor() {
    this.baseURL = '/api/v1/translations';
  }

  /**
   * Translate attraction data to all supported languages
   */
  async translateAttraction(attractionData, sourceLanguage = 'english') {
    try {
      console.log('🔄 === FRESH TRANSLATION API CALL ===');
      console.log('🔄 Timestamp:', new Date().toISOString());
      console.log('🔄 Source Language:', sourceLanguage);
      console.log('🔄 Attraction Data Keys:', Object.keys(attractionData || {}));
      console.log('🔄 Force Refresh Flag:', attractionData?.force_refresh);
      
      // Use the main API instance for consistency with increased timeout for translation
      const response = await api.post(`${this.baseURL}/attraction`, {
        attractionData,
        sourceLanguage
      }, {
        timeout: 90000 // 90s safety margin — the endpoint now parallelizes Sonnet calls (cap 5); raised from 45s
      });

      console.log('✅ Translation API response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('Translation API error:', error);
      
      if (error.response) {
        throw new Error(error.response.data.message || 'Translation failed');
      } else if (error.request) {
        throw new Error('No response from translation service');
      } else {
        throw new Error('Translation request failed');
      }
    }
  }

  /**
   * Save translated attraction data to database
   */
  async saveTranslatedAttraction(attractionId, translatedData, attractionData = null, sourceLanguage = 'english', overwriteLanguages = []) {
    const startTime = Date.now();
    
    try {
      console.log('\n🌐 === TRANSLATION API: SAVE REQUEST ===');
      console.log('⏰ API Request Timestamp:', new Date().toISOString());
      console.log('🎯 Attraction ID:', attractionId);
      console.log('🎯 Source Language:', sourceLanguage);
      console.log('🎯 Has Attraction Data:', !!attractionData);
      console.log('🎯 Translation Languages:', translatedData ? Object.keys(translatedData) : 'none');
      
      if (attractionData) {
        console.log('📋 Attraction Data Summary:', {
          mediaLinksCount: attractionData.media_links?.length || 0,
          hasOperatingHours: !!attractionData.operating_hours,
          hasSocialLinks: !!attractionData.social_links,
          hasPricing: !!(attractionData.price_adult || attractionData.price_child),
          currency: attractionData.currency,
          coordinates: !!attractionData.latitude && !!attractionData.longitude
        });
      }
      
      if (translatedData) {
        Object.keys(translatedData).forEach(lang => {
          console.log(`🌐 ${lang.toUpperCase()} Data:`, {
            hasName: !!translatedData[lang]?.name,
            hasDescription: !!translatedData[lang]?.description,
            hasCategory: !!translatedData[lang]?.category,
            nameLength: translatedData[lang]?.name?.length || 0
          });
        });
      }
      
      const requestData = {
        attractionId,
        translatedData,
        sourceLanguage,
        // Per-language opt-in to overwrite already-populated (reviewed) fields.
        // Default [] = fill-empty-only (backend B2 guard protects reviewed langs).
        overwriteLanguages: Array.isArray(overwriteLanguages) ? overwriteLanguages : []
      };

      console.log('🖊️ overwriteLanguages in save payload:', requestData.overwriteLanguages);

      // Include attraction data if provided (needed for backend validation)
      if (attractionData) {
        requestData.attractionData = attractionData;
      }
      
      console.log('\n📤 Making API request to backend...');
      const response = await api.post(`${this.baseURL}/save`, requestData);

      console.log('\n✅ === API RESPONSE RECEIVED ===');
      console.log('🎯 Response Status:', response.status);
      console.log('🎯 Response Success:', response.data?.success);
      console.log('🎯 Response Message:', response.data?.message);
      console.log('⏱️ API Call Duration:', `${Date.now() - startTime}ms`);
      console.log('📥 Full Response:', response.data);
      console.log('🌐 ======== TRANSLATION API: SUCCESS ========\n');
      
      return response.data;
    } catch (error) {
      console.log('\n❌ === TRANSLATION API: ERROR ===');
      console.log('⏰ Error Timestamp:', new Date().toISOString());
      console.log('❌ Error Type:', error.name);
      console.log('❌ Error Message:', error.message);
      console.log('⏱️ Failed After:', `${Date.now() - startTime}ms`);
      
      if (error.response) {
        console.log('📥 Error Response Status:', error.response.status);
        console.log('📥 Error Response Data:', error.response.data);
        console.log('📥 Error Response Headers:', error.response.headers);
        throw new Error(error.response.data.message || 'Failed to save translation');
      } else if (error.request) {
        console.log('📡 No Response Received:', error.request);
        throw new Error('No response from save service');
      } else {
        console.log('⚙️ Request Setup Error:', error.message);
        throw new Error('Save request failed');
      }
    }
  }

  /**
   * Get translation status for an attraction
   */
  async getTranslationStatus(attractionId) {
    try {
      const response = await api.get(`${this.baseURL}/status/${attractionId}`);
      return response.data;
    } catch (error) {
      console.error('Get translation status error:', error);
      
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to get translation status');
      } else {
        throw new Error('Status request failed');
      }
    }
  }

  /**
   * Get supported languages information
   */
  async getLanguageInfo() {
    try {
      const response = await api.get(`${this.baseURL}/languages`);
      return response.data;
    } catch (error) {
      console.error('Get language info error:', error);
      
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to get language info');
      } else {
        throw new Error('Language info request failed');
      }
    }
  }

  /**
   * Track realistic translation progress based on actual API response.
   * Language list is dynamic — driven by the languages parameter.
   */
  trackRealTranslationProgress(onProgress, actualTranslationPromise, languages = null) {
    return new Promise((resolve) => {
      // Default fallback; callers should pass the real list
      const langs = (languages && languages.length > 0) ? languages : ['Translating...'];
      const totalLangs = langs.length;
      // Scale total animation time proportionally: ~5s per language, min 5s, max 30s
      const totalAnimTime = Math.max(5000, Math.min(totalLangs * 5000, 30000));
      const startTime = Date.now();
      let isTranslationComplete = false;
      let lastProgress = 0;
      let progressInterval = null;
      let lastKnownLanguage = langs[0];
      let lastKnownCompleted = 0;

      onProgress({
        step: 'initializing',
        progress: 0,
        currentLanguage: langs[0],
        completedLanguages: 0
      });

      progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;

        let progress = 0;
        let step = 'initializing';
        let currentLanguage = langs[0];
        let completedLanguages = 0;

        if (elapsed < 2000) {
          // Initializing phase (0-2s)
          progress = Math.min((elapsed / 2000) * 10, 10);
          step = 'initializing';
          currentLanguage = langs[0];
          completedLanguages = 0;
        } else if (elapsed < totalAnimTime) {
          // Translation phase (2s to totalAnimTime)
          const phase = (elapsed - 2000) / (totalAnimTime - 2000);
          progress = 10 + (phase * 75); // 10% to 85%
          step = 'translating';

          const languageProgress = Math.min(phase * totalLangs, totalLangs - 0.1);
          const langIdx = Math.min(Math.floor(languageProgress), totalLangs - 1);
          currentLanguage = langs[langIdx];
          completedLanguages = Math.floor(languageProgress);
        } else {
          // Finalizing phase
          const finalPhase = Math.min((elapsed - totalAnimTime) / 10000, 1);
          progress = 85 + (finalPhase * 10); // 85% to 95%
          step = 'finalizing';
          currentLanguage = langs[totalLangs - 1];
          completedLanguages = totalLangs - 1;
        }

        progress = Math.max(progress, lastProgress);
        lastProgress = progress;

        if (isTranslationComplete) {
          progress = 100;
          step = 'completed';
          completedLanguages = totalLangs;
        }

        lastKnownLanguage = currentLanguage;
        lastKnownCompleted = completedLanguages;

        onProgress({
          step,
          progress: Math.round(progress),
          currentLanguage,
          completedLanguages,
          error: null
        });

        if (elapsed > totalAnimTime + 10000 && !isTranslationComplete) {
          progress = 95;
          step = 'finalizing';
        }
      }, 300);

      if (actualTranslationPromise) {
        actualTranslationPromise
          .then(() => {
            setTimeout(() => {
              onProgress({
                step: 'completed',
                progress: 100,
                currentLanguage: '',
                completedLanguages: totalLangs
              });
              isTranslationComplete = true;
              if (progressInterval) {
                clearInterval(progressInterval);
              }
              resolve(progressInterval);
            }, 500);
          })
          .catch((error) => {
            console.error('Translation promise error:', error);
            onProgress({
              step: 'error',
              progress: Math.max(lastProgress, 0),
              currentLanguage: lastKnownLanguage,
              completedLanguages: lastKnownCompleted,
              error: error.message || 'Translation failed. Please try again.'
            });
            if (progressInterval) {
              clearInterval(progressInterval);
            }
            resolve(progressInterval);
          });
      } else {
        setTimeout(() => {
          isTranslationComplete = true;
          if (progressInterval) {
            clearInterval(progressInterval);
          }
          resolve(progressInterval);
        }, totalAnimTime + 5000);
      }
    });
  }

  /**
   * Translate text using mock translation service
   * TODO: Replace with real translation service when available
   */
  async translateText(text, targetLanguage) {
    try {
      // Mock translations for demonstration
      const mockTranslations = {
        'ar': {
          'VisitMelaka.ai suggestions': 'اقتراحات ملقا الذكية',
          'Show me the best local eats and hidden food gems in Melaka': 'أرني أفضل الأكلات المحلية والجواهر الغذائية المخفية في ملقا',
          'Cultural Sights': 'المعالم الثقافية',
          'Where can I experience Melaka\'s art, heritage, and traditions?': 'أين يمكنني تجربة فن وتراث وتقاليد ملقا؟',
          'Quick Getaway': 'رحلة سريعة',
          'What can I do for a day trip to Melaka?': 'ماذا يمكنني أن أفعل في رحلة يومية إلى ملقا؟',
          'Family Fun': 'مرح العائلة',
          'What are kid-friendly attractions and activities in Melaka?': 'ما هي المعالم والأنشطة المناسبة للأطفال في ملقا؟',
          'Nature Escape': 'هروب طبيعي',
          'Which parks, rivers, eco spots can I explore in Melaka?': 'أي حدائق وأنهار ومواقع بيئية يمكنني استكشافها في ملقا؟',
          'Night Out': 'ليلة خارجية',
          'What can I do in Melaka after sunset?': 'ماذا يمكنني أن أفعل في ملقا بعد غروب الشمس؟',
          'Melaka Adventures': 'مغامرات ملقا',
          'What historical sites should I visit to learn Melaka\'s past?': 'أي المواقع التاريخية يجب أن أزورها لتعلم ماضي ملقا؟',
          'Shopping Spree': 'نزهة تسوق',
          'Where are the best local markets and shopping spots?': 'أين أفضل الأسواق المحلية وأماكن التسوق؟'
        },
        'zh': {
          'VisitMelaka.ai suggestions': '马六甲智能建议',
          'Show me the best local eats and hidden food gems in Melaka': '向我展示马六甲最好的当地美食和隐藏的美食瑰宝',
          'Cultural Sights': '文化景点',
          'Where can I experience Melaka\'s art, heritage, and traditions?': '我可以在哪里体验马六甲的艺术、遗产和传统？',
          'Quick Getaway': '快速度假',
          'What can I do for a day trip to Melaka?': '我可以在马六甲一日游中做什么？',
          'Family Fun': '家庭乐趣',
          'What are kid-friendly attractions and activities in Melaka?': '马六甲有哪些适合儿童的景点和活动？',
          'Nature Escape': '自然逃离',
          'Which parks, rivers, eco spots can I explore in Melaka?': '我可以在马六甲探索哪些公园、河流和生态景点？',
          'Night Out': '夜生活',
          'What can I do in Melaka after sunset?': '日落之后我可以在马六甲做什么？',
          'Melaka Adventures': '马六甲冒险',
          'What historical sites should I visit to learn Melaka\'s past?': '我应该参观哪些历史遗址来了解马六甲的过去？',
          'Shopping Spree': '购物狂欢',
          'Where are the best local markets and shopping spots?': '最好的当地市场和购物点在哪里？'
        },
        'ms': {
          'VisitMelaka.ai suggestions': 'Cadangan Pintar Melaka',
          'Show me the best local eats and hidden food gems in Melaka': 'Tunjukkan kepada saya makanan tempatan terbaik dan permata makanan tersembunyi di Melaka',
          'Cultural Sights': 'Tempat Budaya',
          'Where can I experience Melaka\'s art, heritage, and traditions?': 'Di mana saya boleh mengalami seni, warisan, dan tradisi Melaka?',
          'Quick Getaway': 'Perjalanan Pantas',
          'What can I do for a day trip to Melaka?': 'Apa yang boleh saya lakukan untuk perjalanan sehari ke Melaka?',
          'Family Fun': 'Keseronokan Keluarga',
          'What are kid-friendly attractions and activities in Melaka?': 'Apakah tarikan dan aktiviti mesra kanak-kanak di Melaka?',
          'Nature Escape': 'Pelarian Alam',
          'Which parks, rivers, eco spots can I explore in Melaka?': 'Taman, sungai, dan tempat ekologi mana yang boleh saya terokai di Melaka?',
          'Night Out': 'Malam Keluar',
          'What can I do in Melaka after sunset?': 'Apa yang boleh saya lakukan di Melaka selepas matahari terbenam?',
          'Melaka Adventures': 'Pengembaraan Melaka',
          'What historical sites should I visit to learn Melaka\'s past?': 'Tapak bersejarah mana yang patut saya lawati untuk mempelajari masa lalu Melaka?',
          'Shopping Spree': 'Belanja Raya',
          'Where are the best local markets and shopping spots?': 'Di mana pasar tempatan dan tempat membeli-belah terbaik?'
        },
        'ja': {
          'VisitMelaka.ai suggestions': 'マラッカスマート提案',
          'Show me the best local eats and hidden food gems in Melaka': 'マラッカの最高の地元料理と隠れた美食の宝石を見せてください',
          'Cultural Sights': '文化スポット',
          'Where can I experience Melaka\'s art, heritage, and traditions?': 'マラッカの芸術、遺産、伝統を体験できる場所はどこですか？',
          'Quick Getaway': 'クイックゲットウェイ',
          'What can I do for a day trip to Melaka?': 'マラッカへの日帰り旅行で何ができますか？',
          'Family Fun': 'ファミリー向け',
          'What are kid-friendly attractions and activities in Melaka?': 'マラッカの子供向けアトラクションやアクティビティは何ですか？',
          'Nature Escape': '自然逃避',
          'Which parks, rivers, eco spots can I explore in Melaka?': 'マラッカでどの公園、川、エコスポットを探索できますか？',
          'Night Out': 'ナイトアウト',
          'What can I do in Melaka after sunset?': '日没後にマラッカで何ができますか？',
          'Melaka Adventures': 'マラッカアドベンチャー',
          'What historical sites should I visit to learn Melaka\'s past?': 'マラッカの過去を学ぶためにどの史跡を訪れるべきですか？',
          'Shopping Spree': 'ショッピングスプリー',
          'Where are the best local markets and shopping spots?': '最高の地元市場やショッピングスポットはどこですか？'
        }
      };

      // Get translation for the specific text and language
      const translation = mockTranslations[targetLanguage]?.[text];
      
      if (translation) {
        console.log(`✅ Mock translation: "${text}" → "${translation}" (${targetLanguage})`);
        return translation;
      } else {
        console.log(`⚠️ No mock translation found for "${text}" in ${targetLanguage}, using original`);
        return text; // Fallback to original text
      }
    } catch (error) {
      console.log(`⚠️ Mock translation error for "${text}" to ${targetLanguage}: ${error.message}`);
      return text; // Fallback to original text
    }
  }

  /**
   * Translate home card data to all supported languages using direct update approach
   */
  async translateHomeCard(homeCardData, sourceLanguage = 'english') {
    try {
      console.log('🌐 Starting home card translation...', { homeCardData, sourceLanguage });
      
      // Use the main API instance for consistency
      
      // Use real translation service
      const translatedData = {
        english: {
          title: homeCardData.title,
          text_content: homeCardData.text_content
        },
        arabic: {
          title: await this.translateText(homeCardData.title, 'ar'),
          text_content: await this.translateText(homeCardData.text_content, 'ar')
        },
        chinese: {
          title: await this.translateText(homeCardData.title, 'zh'),
          text_content: await this.translateText(homeCardData.text_content, 'zh')
        },
        malay: {
          title: await this.translateText(homeCardData.title, 'ms'),
          text_content: await this.translateText(homeCardData.text_content, 'ms')
        },
        japanese: {
          title: await this.translateText(homeCardData.title, 'ja'),
          text_content: await this.translateText(homeCardData.text_content, 'ja')
        }
      };

      // Database update data (for saving)
      const dbUpdateData = {
        title_english: translatedData.english.title,
        text_content_english: translatedData.english.text_content,
        title_arabic: translatedData.arabic.title,
        text_content_arabic: translatedData.arabic.text_content,
        title_chinese: translatedData.chinese.title,
        text_content_chinese: translatedData.chinese.text_content,
        title_malay: translatedData.malay.title,
        text_content_malay: translatedData.malay.text_content,
        title_japanese: translatedData.japanese.title,
        text_content_japanese: translatedData.japanese.text_content,
        original_language: 'en',
        translation_status: 'completed',
        translated_at: new Date(),
        translation_version: 1
      };

      // If we have a card ID, update the existing card
      if (homeCardData.id) {
        const updateData = {
          ...homeCardData,
          ...dbUpdateData
        };

        const response = await api.put(`/api/v1/home-cards/${homeCardData.id}`, updateData);
        console.log('🌐 Home card updated with translations:', response.data);
        return {
          success: true,
          data: {
            translatedData: translatedData,
            message: 'Home card updated with translations'
          }
        };
      } else {
        // For new cards, just return the translated data
        console.log('🌐 Translation data prepared for new card');
        return {
          success: true,
          data: {
            translatedData: translatedData,
            message: 'Translation data prepared'
          }
        };
      }
    } catch (error) {
      console.error('🌐 Home card translation API error:', error);
      
      if (error.response) {
        throw new Error(error.response.data.message || 'Home card translation failed');
      } else if (error.request) {
        throw new Error('No response from home card translation service');
      } else {
        throw new Error('Home card translation request failed');
      }
    }
  }

  /**
   * Fetch tenant language names for progress display.
   * Returns e.g. ['Finnish'] for VirQ, or ['Arabic','Chinese','Malay','Japanese'] for Melaka.
   */
  async _getTargetLanguageNames() {
    try {
      const info = await this.getLanguageInfo();
      const supported = info?.data?.supportedLanguages || [];
      // Exclude English (source) — return only target language names
      return supported.filter(l => l.code !== 'en').map(l => l.name);
    } catch (e) {
      console.warn('Could not fetch language info for progress tracker:', e.message);
      return null; // will use fallback in tracker
    }
  }

  /**
   * Complete translation workflow with realistic progress tracking for home cards
   */
  async translateWithProgress(homeCardData, sourceLanguage, onProgress) {
    let progressInterval = null;

    try {
      console.log('🚀 Starting realistic home card translation progress tracking...');

      // Fetch dynamic language list for progress display
      const langNames = await this._getTargetLanguageNames();

      // Start actual translation promise
      const translationPromise = this.translateHomeCard(homeCardData, sourceLanguage);

      // Start realistic progress tracking with actual translation promise
      progressInterval = await this.trackRealTranslationProgress(onProgress, translationPromise, langNames);
      
      // Wait for translation to complete
      const translationResult = await translationPromise;
      
      console.log('✅ Home card translation completed, progress updated to 100%');
      
      return translationResult;
    } catch (error) {
      // Clear progress interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // Update progress to show error state
      onProgress({
        step: 'error',
        progress: 0,
        currentLanguage: '',
        completedLanguages: 0,
        error: error.message
      });
      
      throw error;
    }
  }

  /**
   * Complete translation workflow with realistic progress tracking for attractions
   */
  async translateAttractionWithProgress(attractionData, sourceLanguage, onProgress) {
    let progressInterval = null;

    try {
      console.log('🚀 Starting realistic attraction translation progress tracking...');

      // Fetch dynamic language list for progress display
      const langNames = await this._getTargetLanguageNames();

      // Start actual translation promise
      const translationPromise = this.translateAttraction(attractionData, sourceLanguage);

      // Start realistic progress tracking with actual translation promise
      progressInterval = await this.trackRealTranslationProgress(onProgress, translationPromise, langNames);
      
      // Wait for translation to complete
      const translationResult = await translationPromise;
      
      console.log('✅ Attraction translation completed, progress updated to 100%');
      
      return translationResult;
    } catch (error) {
      // Clear progress interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // Update progress to show error state
      onProgress({
        step: 'error',
        progress: 0,
        currentLanguage: '',
        completedLanguages: 0,
        error: error.message
      });
      
      throw error;
    }
  }
}

const translationAPI = new TranslationAPI();
export default translationAPI;